/**
 * Vista imprimible de una propuesta comercial.
 * v2: logo TALENGERS multicolor 50% más grande, pie charts SVG (alineados),
 * redes con logos + link clicable en el PDF, tabla de precios múltiples.
 *
 * Cada .pdf-page se snapshottea con html2canvas y se agrega al PDF con jsPDF.
 * Los elementos con data-pdf-link se registran como anotaciones clicables
 * después del snapshot (ver pdfGenerator.js).
 */
import React from 'react';
import { Instagram } from 'lucide-react';
import { FaTiktok, FaYoutube } from 'react-icons/fa';

// Paleta de marca Talengers (multicolor)
const CHART_COLORS = ['#F4A261', '#457B9D', '#1B6B3A', '#FF6EC7', '#00F0FF', '#E63946'];
const TAG_COLORS = ['#FF6EC7', '#00F0FF', '#39FF14', '#F4A261', '#457B9D', '#E63946'];
const LOGO_COLORS = ['#F4A261', '#E63946', '#FF6EC7', '#1B6B3A', '#00F0FF', '#457B9D', '#F4A261', '#E63946', '#1B6B3A'];

const parseSexo = (str) => {
  if (!str) return [];
  return str.split(',').map((s) => s.trim()).map((part, i) => {
    const m = part.match(/(.+?):\s*([\d.]+)%/);
    if (m) return { label: m[1].trim(), value: parseFloat(m[2]), color: i === 0 ? '#FF6EC7' : '#457B9D' };
    return null;
  }).filter(Boolean);
};

const parseEdades = (str) => {
  if (!str) return [];
  return str.split(',').map((s) => s.trim()).map((part, i) => {
    const m1 = part.match(/([\d-]+)\s*\((\d+)%\)/);
    const m2 = part.match(/(\d+)%\s*\(([\d-]+)\)/);
    if (m1) return { label: m1[1], value: parseFloat(m1[2]), color: CHART_COLORS[i % CHART_COLORS.length] };
    if (m2) return { label: m2[2], value: parseFloat(m2[1]), color: CHART_COLORS[i % CHART_COLORS.length] };
    return null;
  }).filter(Boolean);
};

// Canvas-based pie chart — snapshottea perfecto con html2canvas.
// Usa 2D context (SVGs a veces no se rasterizan bien en html2canvas).
const CanvasPieChart = ({ data, size = 90 }) => {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data || data.length === 0) return;
    const dpr = 2;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size, size);
    const cx = size / 2, cy = size / 2, r = size / 2 - 1;
    const total = data.reduce((s, d) => s + d.value, 0);
    if (total === 0) return;
    if (data.length === 1) {
      // Single slice: full circle
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, 2 * Math.PI);
      ctx.fillStyle = data[0].color;
      ctx.fill();
      return;
    }
    let start = -Math.PI / 2; // start at top
    data.forEach((d) => {
      const angle = (d.value / total) * 2 * Math.PI;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, r, start, start + angle);
      ctx.closePath();
      ctx.fillStyle = d.color;
      ctx.fill();
      start += angle;
    });
  }, [data, size]);
  if (!data || !data.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <canvas ref={canvasRef} style={{ flexShrink: 0, display: 'block' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
        {data.map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.1 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: d.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#d1d5db', whiteSpace: 'nowrap' }}>
              <strong style={{ color: 'white', fontWeight: 700 }}>{d.value}%</strong>
              <span style={{ marginLeft: 6 }}>{d.label}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Logo multicolor letra por letra
const MulticolorLogo = ({ size = 108, letterSpacing = 4 }) => (
  <div
    style={{
      fontFamily: "'Bebas Neue', sans-serif",
      fontSize: size,
      letterSpacing,
      lineHeight: 1,
      display: 'flex',
      whiteSpace: 'nowrap',
    }}
  >
    {'TALENGERS'.split('').map((ch, i) => (
      <span key={i} style={{ color: LOGO_COLORS[i % LOGO_COLORS.length] }}>{ch}</span>
    ))}
  </div>
);

// Social pill — foto, handle visible, y anotación clicable en el PDF vía data-pdf-link
const socialHandle = (url) => {
  if (!url) return '';
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, '').replace(/^\/+/, '');
    return path ? `@${path.split('/').pop().replace(/^@/, '')}` : u.hostname;
  } catch { return url.replace(/^https?:\/\//, '').replace(/\/$/, ''); }
};

const SocialPill = ({ platform, url }) => {
  if (!url) return null;
  const cfg = {
    instagram: { icon: <Instagram size={14} color="white" />, color: '#E4405F', label: 'Instagram' },
    tiktok:    { icon: <FaTiktok size={12} color="white" />, color: '#000000', label: 'TikTok' },
    youtube:   { icon: <FaYoutube size={14} color="white" />, color: '#FF0000', label: 'YouTube' },
  }[platform];
  if (!cfg) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-pdf-link={url}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        color: 'white',
        fontSize: 13,
        fontWeight: 600,
        textDecoration: 'none',
        lineHeight: 1.2,
      }}
    >
      <span
        style={{
          width: 26, height: 26, borderRadius: '50%',
          backgroundColor: cfg.color,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {cfg.icon}
      </span>
      <span style={{ whiteSpace: 'nowrap' }}>{socialHandle(url)}</span>
    </a>
  );
};

const CoverPage = ({ propuesta, fecha, total }) => {
  // Fallback: si es una propuesta vieja usar cliente_nombre como marca.
  const marca = propuesta.marca || propuesta.cliente_nombre || '';
  const campana = propuesta.campana || '';
  const comercial = propuesta.comercial_nombre || '';
  const esAcuerdo = (propuesta.tipo || 'propuesta') === 'acuerdo';
  // Siempre recalcular el scope desde los talentos actuales (ignorar valores legacy).
  const autoScope = computeScopeFromTalentos(propuesta.talentos);

  const leftCol = [
    { label: 'Agencia', value: propuesta.agencia },
    { label: 'Contacto', value: propuesta.contacto },
    { label: 'Comercial Talengers', value: comercial },
  ].filter((f) => f.value);

  const rightCol = [
    { label: 'Scope', value: autoScope },
    { label: 'Temporalidad', value: propuesta.temporalidad },
    { label: 'Plazo de pago', value: propuesta.plazo_pago },
  ].filter((f) => f.value);

  return (
    <div
      className="pdf-page"
      style={{
        width: 794, height: 1123,
        backgroundColor: '#0a0a0a',
        color: 'white',
        padding: '80px 60px 40px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      {/* Logo centered + subtitle centered under it */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <MulticolorLogo size={108} letterSpacing={4} />
        <div style={{ marginTop: 32, fontSize: 18, color: esAcuerdo ? '#4ADE80' : '#9CA3AF', letterSpacing: 6, textTransform: 'uppercase', fontWeight: 700 }}>
          {esAcuerdo ? 'Acuerdo Comercial' : 'Propuesta Comercial'}
        </div>
      </div>

      {/* Marca (grande) + Campaña (30% más chica, centrada debajo) */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 68, lineHeight: 1.02, letterSpacing: 1 }}>
          {marca || 'Marca'}
        </div>
        {campana && (
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, letterSpacing: 2, marginTop: 8, color: '#F4A261' }}>
            {campana}
          </div>
        )}
      </div>

      {/* Dos columnas de datos */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '32px 80px',
        borderTop: '1px solid #333',
        borderBottom: '1px solid #333',
        padding: '32px 60px',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {leftCol.map((f, i) => (
            <div key={i}>
              <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1.5 }}>{f.label}</div>
              <div style={{ fontSize: 17, marginTop: 4, color: '#e5e7eb' }}>{f.value}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          {rightCol.map((f, i) => (
            <div key={i}>
              <div style={{ fontSize: 11, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1.5 }}>{f.label}</div>
              <div style={{ fontSize: 17, marginTop: 4, color: '#e5e7eb' }}>{f.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer: brand line izquierda, fecha a la derecha */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: 11, color: '#6B7280' }}>
        <div>
          Talengers Agency · talengersok.com
          <div style={{ fontSize: 10, color: '#4B5563', marginTop: 3 }}>{total} talento{total !== 1 ? 's' : ''} incluido{total !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ textAlign: 'right' }}>{fecha}</div>
      </div>
    </div>
  );
};

const PriceRow = ({ item }) => {
  const unit = parseValue(item.precio);
  const qty = parseQty(item.cantidad);
  const total = unit * qty;
  return (
    <tr style={{ borderTop: '1px solid #1f1f1f' }}>
      <td style={{ padding: '8px 10px', fontSize: 12, color: '#e5e7eb', fontWeight: 600 }}>{item.red || '—'}</td>
      <td style={{ padding: '8px 10px', fontSize: 12, color: '#e5e7eb', textAlign: 'center' }}>{item.cantidad || '—'}</td>
      <td style={{ padding: '8px 10px', fontSize: 12, color: '#e5e7eb' }}>{item.tipo || '—'}</td>
      <td style={{ padding: '8px 10px', fontSize: 12, color: '#d1d5db', textAlign: 'right', whiteSpace: 'nowrap' }}>{unit > 0 ? formatValue(unit) : (item.precio || '—')}</td>
      <td style={{ padding: '8px 10px', fontSize: 13, color: '#F4A261', fontWeight: 700, textAlign: 'right', whiteSpace: 'nowrap' }}>{total > 0 ? formatValue(total) : '—'}</td>
      <td style={{ padding: '8px 10px', fontSize: 11, color: '#9CA3AF' }}>{item.notas || ''}</td>
    </tr>
  );
};

const TalentPage = ({ t, index, total }) => {
  const verticals = t.vertical ? t.vertical.split(',').map((v) => v.trim()) : [];
  const paises = t.paises ? t.paises.split(',').map((p) => p.trim()) : [];
  const sexo = parseSexo(t.sexo);
  const edades = parseEdades(t.edades);

  // Fallback: si hay precio/condiciones legacy y no hay items_precio, mostrarlo como una única fila
  let precios = Array.isArray(t.items_precio) ? t.items_precio.filter(
    (p) => p.red || p.cantidad || p.tipo || p.precio || p.notas
  ) : [];
  if (precios.length === 0 && (t.precio || t.condiciones)) {
    precios = [{ red: '', cantidad: '', tipo: '', precio: t.precio || '', notas: t.condiciones || '' }];
  }

  return (
    <div
      className="pdf-page"
      style={{
        width: 794, height: 1123,
        backgroundColor: '#0a0a0a',
        color: 'white',
        padding: 40,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header brand strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 16, borderBottom: '1px solid #222' }}>
        <MulticolorLogo size={28} letterSpacing={2} />
        <div style={{ fontSize: 11, color: '#6B7280' }}>Talento {index + 1} de {total}</div>
      </div>

      {/* Talent header: photo + name + verticals + socials */}
      <div style={{ display: 'flex', gap: 20, marginTop: 20 }}>
        {t.foto_url ? (
          <div
            aria-label={t.nombre}
            style={{
              width: 200,
              height: 200,
              flexShrink: 0,
              borderRadius: 12,
              backgroundColor: '#111',
              backgroundImage: `url(${t.foto_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        ) : (
          <div style={{ width: 200, height: 200, backgroundColor: '#111', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151', fontSize: 12 }}>Sin foto</div>
        )}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, letterSpacing: 0.5, lineHeight: 1 }}>
            {t.nombre}
          </div>
          {verticals.length > 0 && (
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 6, letterSpacing: 0.5 }}>
              {verticals.join(' · ')}
            </div>
          )}
          {t.descripcion && (
            <p style={{ fontSize: 12, color: '#d1d5db', lineHeight: 1.45, marginTop: 10, marginBottom: 0 }}>{t.descripcion}</p>
          )}
          {/* Redes: una columna por red con logo+@handle arriba y seguidores debajo */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 14 }}>
            {t.instagram_url && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <SocialPill platform="instagram" url={t.instagram_url} />
                {t.instagram_followers && (
                  <div style={{ fontSize: 12, color: '#9CA3AF', paddingLeft: 34 }}>
                    <strong style={{ color: 'white', fontWeight: 700 }}>{formatFollowers(t.instagram_followers)}</strong> seguidores
                  </div>
                )}
              </div>
            )}
            {t.tiktok_url && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <SocialPill platform="tiktok" url={t.tiktok_url} />
                {t.tiktok_followers && (
                  <div style={{ fontSize: 12, color: '#9CA3AF', paddingLeft: 34 }}>
                    <strong style={{ color: 'white', fontWeight: 700 }}>{formatFollowers(t.tiktok_followers)}</strong> seguidores
                  </div>
                )}
              </div>
            )}
            {t.youtube_url && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <SocialPill platform="youtube" url={t.youtube_url} />
                {t.youtube_subscribers && (
                  <div style={{ fontSize: 12, color: '#9CA3AF', paddingLeft: 34 }}>
                    <strong style={{ color: 'white', fontWeight: 700 }}>{formatFollowers(t.youtube_subscribers)}</strong> suscriptores
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Analytics: pies sin cajas, alineados limpiamente */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginTop: 28 }}>
        {sexo.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#F4A261', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 10 }}>Género</div>
            <CanvasPieChart data={sexo} size={80} />
          </div>
        )}
        {edades.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#F4A261', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 10 }}>Edades</div>
            <CanvasPieChart data={edades} size={80} />
          </div>
        )}
      </div>

      {paises.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#F4A261', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>Países</div>
          <div style={{ fontSize: 13, color: '#e5e7eb' }}>
            {paises.join(' · ')}
          </div>
        </div>
      )}

      {/* Presupuesto */}
      <div style={{ marginTop: 32 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#F4A261', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 10 }}>Presupuesto</div>
        {precios.length === 0 ? (
          <div style={{ padding: 12, textAlign: 'center', color: '#6B7280', fontSize: 12, border: '1px dashed #333', borderRadius: 8 }}>Sin precios cargados</div>
        ) : (() => {
          const talentTotal = precios.reduce((s, it) => s + (parseValue(it.precio) * parseQty(it.cantidad)), 0);
          return (
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff05', borderRadius: 8, overflow: 'hidden' }}>
            <thead>
              <tr style={{ backgroundColor: '#1B6B3A' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>RRSS</th>
                <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>Cant.</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>Tipo</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>V. Unitario</th>
                <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700, backgroundColor: '#0f4a25' }}>V. Total</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>Notas</th>
              </tr>
            </thead>
            <tbody>
              {precios.map((it, i) => <PriceRow key={i} item={it} />)}
              {precios.length > 1 && talentTotal > 0 && (
                <tr style={{ backgroundColor: '#0f4a25', borderTop: '2px solid #1B6B3A' }}>
                  <td colSpan={4} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'white', textAlign: 'right' }}>Total talento</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 14, color: '#F4A261', fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{formatValue(talentTotal)}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
          );
        })()}
        {/* Aclaraciones del comercial (ej: descuentos, condiciones especiales) */}
        {t.aclaraciones && t.aclaraciones.trim() && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>Aclaraciones</div>
            <div style={{ fontSize: 12, color: '#e5e7eb', lineHeight: 1.5, whiteSpace: 'pre-wrap', padding: 12, backgroundColor: '#ffffff05', borderLeft: '3px solid #F4A261', borderRadius: 4 }}>
              {t.aclaraciones}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============ EXPRESS VARIANT ============
// Formato compacto: tabla con 1 fila por talento y columnas por red social.
// Cabecera: Agencia (arriba) · Marca (grande) · Campaña (abajo).
// Cierra con aclaraciones globales y footer con fecha.

export const parseValue = (str) => {
  // Extrae el número principal de un string tipo "$500 USD" o "1500"
  if (!str) return 0;
  const m = String(str).replace(/\./g, '').replace(/,/g, '.').match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : 0;
};

export const formatValue = (num) => {
  if (!num) return '—';
  return '$' + num.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

export const parseQty = (str) => {
  if (!str) return 1;
  const m = String(str).replace(',', '.').match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : 1;
};

// ---------- Followers formatter (1.200.000 → 1.2M · 270.859 → 270K) ----------
// Parsea strings tipo "1,200,000", "1.200.000", "1.2M", "185K", "185,672" y devuelve
// un string compacto: >=1M usa "X.YM" (1 decimal, sin ceros trailing), >=1K usa "XK".
export const formatFollowers = (raw) => {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (!s) return '';
  // Si ya viene con sufijo M/K/B, lo dejamos como está (normalizado)
  const suffixed = s.match(/^([\d.,]+)\s*([kKmMbB])$/);
  if (suffixed) {
    // Formato con sufijo: no hay separador de miles, sólo un decimal (`,` o `.`)
    const numStr = suffixed[1].replace(',', '.');
    const num = parseFloat(numStr);
    const suf = suffixed[2].toUpperCase();
    return isNaN(num) ? s : `${String(num).replace('.', ',')}${suf}`;
  }
  // Extraer dígitos ignorando separadores de miles
  const digits = s.replace(/[^\d]/g, '');
  if (!digits) return s;
  const n = parseInt(digits, 10);
  if (isNaN(n)) return s;
  if (n >= 1_000_000) {
    const val = n / 1_000_000;
    // 1 decimal, locale AR (coma). Sin ceros trailing.
    const oneDec = Math.floor(val * 10) / 10;
    const str = oneDec % 1 === 0 ? String(Math.floor(oneDec)) : String(oneDec).replace('.', ',');
    return `${str}M`;
  }
  if (n >= 1_000) {
    return `${Math.floor(n / 1_000)}K`;
  }
  return String(n);
};

// ---------- Scope auto-generation ----------
// Genera un string tipo: "Ana Ejemplo: 2 Reel (IG), 3 Stories (IG) · Juan: 1 Post (TT)"
// a partir de los talentos + items_precio cargados por el comercial.
const RED_SHORT = { Instagram: 'IG', TikTok: 'TT', YouTube: 'YT' };
export const computeScopeFromTalentos = (talentos) => {
  if (!Array.isArray(talentos) || talentos.length === 0) return '';
  const parts = talentos.map((t) => {
    const items = Array.isArray(t.items_precio) ? t.items_precio.filter((it) => (it.tipo || it.red)) : [];
    if (items.length === 0) return null;
    const chunks = items.map((it) => {
      const qty = parseQty(it.cantidad);
      const q = qty > 0 ? Math.round(qty) : 1;
      const tipo = (it.tipo || '').trim();
      const redShort = RED_SHORT[it.red] || it.red || '';
      if (!tipo && !redShort) return null;
      const label = tipo || redShort;
      const suffix = tipo && redShort ? ` (${redShort})` : '';
      return `${q} ${label}${suffix}`;
    }).filter(Boolean);
    if (chunks.length === 0) return null;
    const nombre = (t.nombre || '').trim();
    return nombre ? `${nombre}: ${chunks.join(', ')}` : chunks.join(', ');
  }).filter(Boolean);
  return parts.join(' · ');
};


const ExpressPage = ({ propuesta, fecha }) => {
  const marca = propuesta.marca || propuesta.cliente_nombre || '';
  const talentos = propuesta.talentos || [];
  const esAcuerdo = (propuesta.tipo || 'propuesta') === 'acuerdo';
  const autoScope = computeScopeFromTalentos(talentos);

  // Header info left-aligned column
  const header = [
    { label: 'Agencia', value: propuesta.agencia },
    { label: 'Marca', value: marca },
    { label: 'Campaña', value: propuesta.campana },
    { label: 'Scope', value: autoScope },
    { label: 'Plazo de pago', value: propuesta.plazo_pago },
  ].filter((f) => f.value);

  // Flatten all price items — one row per action, tagged with talent info
  const flatRows = [];
  talentos.forEach((t) => {
    const items = Array.isArray(t.items_precio) ? t.items_precio.filter(
      (it) => it.red || it.tipo || it.precio || it.cantidad
    ) : [];
    if (items.length === 0 && t.precio) {
      // Legacy fallback (older propuestas)
      const unit = parseValue(t.precio);
      flatRows.push({ t, first: true, red: '', cantidad: 1, tipo: '', unit, total: unit });
    } else {
      items.forEach((it, idx) => {
        const unit = parseValue(it.precio);
        const qty = parseQty(it.cantidad) || 1;
        flatRows.push({ t, first: idx === 0, red: it.red || '', cantidad: qty, tipo: it.tipo || '', unit, total: unit * qty });
      });
    }
  });

  const grandTotal = flatRows.reduce((s, r) => s + r.total, 0);

  // Collect aclaraciones
  const allAcl = talentos.map((t) => (t.aclaraciones || '').trim()).filter(Boolean);

  return (
    <div
      className="pdf-page"
      style={{
        width: 794, height: 1123,
        backgroundColor: '#0a0a0a',
        color: 'white',
        padding: 48,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header brand strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 14, borderBottom: '1px solid #222' }}>
        <MulticolorLogo size={28} letterSpacing={2} />
        <div style={{ fontSize: 11, color: esAcuerdo ? '#4ADE80' : '#6B7280', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700 }}>{esAcuerdo ? 'Acuerdo Express' : 'Presupuesto Express'}</div>
      </div>

      {/* Columna izquierda de datos: Label + Value */}
      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {header.map((f, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 12, fontSize: 15 }}>
            <span style={{ minWidth: 130, color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, fontSize: 12 }}>{f.label}:</span>
            <span style={{ color: 'white', fontWeight: 500 }}>{f.value}</span>
          </div>
        ))}
      </div>

      {/* Tabla plana: una fila por acción */}
      <div style={{ marginTop: 28 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff05', borderRadius: 8, overflow: 'hidden' }}>
          <thead>
            <tr style={{ backgroundColor: '#1B6B3A' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>Talento</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>RRSS</th>
              <th style={{ padding: '10px 6px', textAlign: 'center', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>Cant.</th>
              <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>Tipo</th>
              <th style={{ padding: '10px 8px', textAlign: 'right', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>V. Unitario</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700, backgroundColor: '#0f4a25' }}>V. Total</th>
            </tr>
          </thead>
          <tbody>
            {flatRows.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid #1f1f1f' }}>
                <td style={{ padding: '8px 12px' }}>
                  {r.first ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {r.t.foto_url ? (
                        <div style={{ width: 34, height: 34, borderRadius: '50%', backgroundColor: '#111', backgroundImage: `url(${r.t.foto_url})`, backgroundSize: 'cover', backgroundPosition: 'center', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 34, height: 34, borderRadius: '50%', backgroundColor: '#222', flexShrink: 0 }} />
                      )}
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'white', whiteSpace: 'nowrap' }}>{r.t.nombre}</div>
                    </div>
                  ) : null}
                </td>
                <td style={{ padding: '8px', fontSize: 12, color: '#e5e7eb' }}>
                  {(() => {
                    // Link a la red social del talento (IG/TT/YT) — cliqueable en el PDF.
                    // data-pdf-link es leído por pdfGenerator.js para agregar la
                    // link annotation cliqueable sobre la imagen renderizada.
                    const red = r.red || '';
                    if (!red) return '—';
                    const map = { Instagram: r.t.instagram_url, TikTok: r.t.tiktok_url, YouTube: r.t.youtube_url };
                    const url = map[red] || '';
                    if (!url) return red;
                    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
                    return (
                      <a href={href} target="_blank" rel="noopener noreferrer" data-pdf-link={href} style={{ color: '#F4A261', fontWeight: 700, textDecoration: 'underline', display: 'inline-block', padding: '2px 4px' }}>{red}</a>
                    );
                  })()}
                </td>
                <td style={{ padding: '8px 6px', textAlign: 'center', fontSize: 12, color: '#e5e7eb' }}>{r.cantidad || '—'}</td>
                <td style={{ padding: '8px', fontSize: 12, color: '#e5e7eb' }}>{r.tipo || '—'}</td>
                <td style={{ padding: '8px', textAlign: 'right', fontSize: 12, color: '#d1d5db', whiteSpace: 'nowrap' }}>{formatValue(r.unit)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 13, color: '#F4A261', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatValue(r.total)}</td>
              </tr>
            ))}
            {flatRows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#6B7280', fontSize: 12 }}>Sin ítems cargados</td></tr>
            )}
            <tr style={{ backgroundColor: '#0f4a25', borderTop: '2px solid #1B6B3A' }}>
              <td colSpan={5} style={{ padding: '10px 12px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'white', textAlign: 'right' }}>Total General</td>
              <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 18, color: '#F4A261', fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, whiteSpace: 'nowrap' }}>{formatValue(grandTotal)}</td>
            </tr>
            {(() => {
              // Descuento paquete completo: se muestra sólo si el comercial cargó un %
              const pct = Math.max(0, Math.min(100, Number(propuesta.descuento_porcentaje) || 0));
              if (pct <= 0) return null;
              const descuento = grandTotal * (pct / 100);
              const totalFinal = grandTotal - descuento;
              return (
                <>
                  <tr style={{ backgroundColor: '#1a0f2e', borderTop: '1px solid #4c1d95' }}>
                    <td colSpan={5} style={{ padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#c4b5fd', textAlign: 'right' }}>
                      Descuento paquete completo ({pct}%)
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, color: '#c4b5fd', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      −{formatValue(descuento)}
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#0f4a25', borderTop: '2px solid #F4A261' }}>
                    <td colSpan={5} style={{ padding: '12px 12px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: 'white', textAlign: 'right' }}>Total Final</td>
                    <td style={{ padding: '12px 12px', textAlign: 'right', fontSize: 22, color: '#F4A261', fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, whiteSpace: 'nowrap' }}>{formatValue(totalFinal)}</td>
                  </tr>
                </>
              );
            })()}
          </tbody>
        </table>
      </div>

      {/* Aclaraciones */}
      {allAcl.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>Aclaraciones</div>
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#e5e7eb', lineHeight: 1.6 }}>
            {allAcl.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#6B7280', marginTop: 'auto', paddingTop: 20, borderTop: '1px solid #222' }}>
        <div>
          {propuesta.comercial_nombre ? `Comercial Talengers: ${propuesta.comercial_nombre}` : 'Talengers Agency · talengersok.com'}
          {propuesta.contacto && <span style={{ marginLeft: 12 }}>· Contacto: {propuesta.contacto}</span>}
          {propuesta.temporalidad && <span style={{ marginLeft: 12 }}>· {propuesta.temporalidad}</span>}
        </div>
        <div>{fecha}</div>
      </div>
    </div>
  );
};

const SummaryPage = ({ propuesta, fecha }) => {
  const marca = propuesta.marca || propuesta.cliente_nombre || '';
  const talentos = propuesta.talentos || [];
  const esAcuerdo = (propuesta.tipo || 'propuesta') === 'acuerdo';
  const autoScope = computeScopeFromTalentos(talentos);

  const rows = talentos.map((t) => {
    const items = Array.isArray(t.items_precio) ? t.items_precio : [];
    let subtotal = 0;
    let itemsCount = 0;
    items.forEach((it) => {
      const unit = parseValue(it.precio);
      const qty = parseQty(it.cantidad);
      if (unit > 0) { subtotal += unit * qty; itemsCount += 1; }
    });
    // Legacy fallback for older propuestas without items_precio
    if (itemsCount === 0 && t.precio) {
      const u = parseValue(t.precio);
      if (u > 0) { subtotal = u; itemsCount = 1; }
    }
    return { nombre: t.nombre || 'Talento', foto_url: t.foto_url, itemsCount, subtotal };
  });

  const grandTotal = rows.reduce((s, r) => s + r.subtotal, 0);

  return (
    <div
      className="pdf-page"
      style={{
        width: 794, height: 1123,
        backgroundColor: '#0a0a0a',
        color: 'white',
        padding: 48,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header brand strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 14, borderBottom: '1px solid #222' }}>
        <MulticolorLogo size={28} letterSpacing={2} />
        <div style={{ fontSize: 11, color: '#6B7280', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700 }}>Resumen General</div>
      </div>

      {/* Title */}
      <div style={{ marginTop: 36 }}>
        <div style={{ fontSize: 11, color: '#9CA3AF', letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700 }}>
          {esAcuerdo ? 'Acuerdo cerrado' : 'Cierre de propuesta'}
        </div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 56, lineHeight: 1, marginTop: 6 }}>
          {marca || 'Propuesta'}
        </div>
        {propuesta.campana && (
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, letterSpacing: 1.5, marginTop: 4, color: '#F4A261' }}>
            {propuesta.campana}
          </div>
        )}
      </div>

      {/* Talent totals table */}
      <div style={{ marginTop: 36 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff05', borderRadius: 8, overflow: 'hidden' }}>
          <thead>
            <tr style={{ backgroundColor: '#1B6B3A' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>Talento</th>
              <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700 }}>Ítems</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'white', fontWeight: 700, backgroundColor: '#0f4a25' }}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: '1px solid #1f1f1f' }}>
                <td style={{ padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {r.foto_url ? (
                      <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#111', backgroundImage: `url(${r.foto_url})`, backgroundSize: 'cover', backgroundPosition: 'center', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 36, height: 36, borderRadius: '50%', backgroundColor: '#222', flexShrink: 0 }} />
                    )}
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>{r.nombre}</div>
                  </div>
                </td>
                <td style={{ padding: '10px 8px', textAlign: 'center', fontSize: 13, color: '#d1d5db' }}>{r.itemsCount || '—'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: 14, color: '#F4A261', fontWeight: 700, whiteSpace: 'nowrap' }}>{r.subtotal > 0 ? formatValue(r.subtotal) : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={3} style={{ padding: 20, textAlign: 'center', color: '#6B7280', fontSize: 12 }}>Sin talentos cargados</td></tr>
            )}
            <tr style={{ backgroundColor: '#0f4a25', borderTop: '2px solid #1B6B3A' }}>
              <td colSpan={2} style={{ padding: '14px 12px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: 'white', textAlign: 'right' }}>Total General</td>
              <td style={{ padding: '14px 12px', textAlign: 'right', fontSize: 26, color: '#F4A261', fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, whiteSpace: 'nowrap' }}>{formatValue(grandTotal)}</td>
            </tr>
            {(() => {
              const pct = Math.max(0, Math.min(100, Number(propuesta.descuento_porcentaje) || 0));
              if (pct <= 0) return null;
              const descuento = grandTotal * (pct / 100);
              const totalFinal = grandTotal - descuento;
              return (
                <>
                  <tr style={{ backgroundColor: '#1a0f2e', borderTop: '1px solid #4c1d95' }}>
                    <td colSpan={2} style={{ padding: '12px 12px', fontSize: 13, fontWeight: 600, color: '#c4b5fd', textAlign: 'right' }}>
                      Descuento paquete completo ({pct}%)
                    </td>
                    <td style={{ padding: '12px 12px', textAlign: 'right', fontSize: 18, color: '#c4b5fd', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      −{formatValue(descuento)}
                    </td>
                  </tr>
                  <tr style={{ backgroundColor: '#0f4a25', borderTop: '2px solid #F4A261' }}>
                    <td colSpan={2} style={{ padding: '14px 12px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: 'white', textAlign: 'right' }}>Total Final</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', fontSize: 30, color: '#F4A261', fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1, whiteSpace: 'nowrap' }}>{formatValue(totalFinal)}</td>
                  </tr>
                </>
              );
            })()}
          </tbody>
        </table>
      </div>

      {/* Datos de la campaña recap */}
      <div style={{ marginTop: 28, padding: 16, backgroundColor: '#ffffff05', borderRadius: 8, border: '1px solid #1f1f1f' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#F4A261', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 10 }}>Datos de la campaña</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: 12, color: '#e5e7eb' }}>
          {propuesta.agencia && <div><span style={{ color: '#6B7280' }}>Agencia: </span>{propuesta.agencia}</div>}
          {propuesta.contacto && <div><span style={{ color: '#6B7280' }}>Contacto: </span>{propuesta.contacto}</div>}
          {autoScope && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#6B7280' }}>Scope: </span>{autoScope}</div>}
          {propuesta.temporalidad && <div><span style={{ color: '#6B7280' }}>Temporalidad: </span>{propuesta.temporalidad}</div>}
          {propuesta.plazo_pago && <div><span style={{ color: '#6B7280' }}>Plazo de pago: </span>{propuesta.plazo_pago}</div>}
          {propuesta.comercial_nombre && <div><span style={{ color: '#6B7280' }}>Comercial: </span>{propuesta.comercial_nombre}</div>}
          <div><span style={{ color: '#6B7280' }}>Tipo: </span><strong style={{ color: esAcuerdo ? '#4ADE80' : '#FDBA74' }}>{esAcuerdo ? 'Acuerdo' : 'Propuesta'}</strong></div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#6B7280', marginTop: 'auto', paddingTop: 20, borderTop: '1px solid #222' }}>
        <div>Talengers Agency · talengersok.com</div>
        <div>{fecha}</div>
      </div>
    </div>
  );
};

const PropuestaPreview = React.forwardRef(({ propuesta, variant = 'full' }, ref) => {
  if (!propuesta) return null;
  const fecha = new Date(propuesta.fecha_creacion || Date.now()).toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' });
  if (variant === 'express') {
    return (
      <div ref={ref} style={{ position: 'fixed', left: -99999, top: 0 }}>
        <ExpressPage propuesta={propuesta} fecha={fecha} />
      </div>
    );
  }
  return (
    <div ref={ref} style={{ position: 'fixed', left: -99999, top: 0 }}>
      <CoverPage
        propuesta={propuesta}
        fecha={fecha}
        total={propuesta.talentos?.length || 0}
      />
      {(propuesta.talentos || []).map((t, i) => (
        <TalentPage key={i} t={t} index={i} total={propuesta.talentos.length} />
      ))}
      <SummaryPage propuesta={propuesta} fecha={fecha} />
    </div>
  );
});

PropuestaPreview.displayName = 'PropuestaPreview';
export default PropuestaPreview;
